import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import { get, set } from "lodash";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  MdOutlineFreeCancellation,
  MdOutlineFrontHand,
  MdOutlinePinDrop,
  MdOutlineShareLocation,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  initialValues,
  setPercent,
  bindField,
  isFieldRequired,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const contractMoreThanOneYear = get(
    initialValues,
    "project.fields.contractMoreThanOneYear",
    false,
  );

  const terminationRequestDisabledFields = {
    [false]: {
      contractNumber: true,
      brokerageMandate: {
        currentCompany: true,
        lastName: true,
        firstName: true,
      },
    },
    [true]: {
      contractNumber: false,
      brokerageMandate: {
        currentCompany: false,
        lastName: false,
        firstName: false,
      },
    },
  };

  const applyTerminationRequestDisabledState = (nextDisabled) => {
    flushSync(() => {
      setDisabled((prev) => {
        const next = {
          ...prev,
          project: {
            ...prev.project,
            terminationRequest: nextDisabled,
          },
        };
        setPercent(initialValues, next);
        return next;
      });
    });
  };

  const handleContractMoreThan12MonthsChange = (checked) => {
    set(initialValues, "project.fields.contractMoreThanOneYear", checked);
    if (checked) {
      set(initialValues, "project.terminationRequest", {
        id: cuid(),
        brokerageMandate: { id: cuid() },
      });
      set(
        initialValues,
        "project.terminationRequest.brokerageMandate.lastName",
        get(initialValues, "project.contact.user.lastname"),
      );
      set(
        initialValues,
        "project.terminationRequest.brokerageMandate.firstName",
        get(initialValues, "project.contact.user.firstname"),
      );
    } else {
      set(initialValues, "project.fields.selfOccupation", undefined);
      set(initialValues, "project.terminationRequest", null);
    }

    applyTerminationRequestDisabledState(
      get(terminationRequestDisabledFields, checked),
    );
  };

  const contractNumberPath = "project.terminationRequest.contractNumber";
  const contractNumberField = bindField(contractNumberPath, {
    label: SUBSCRIPTION.FORM.CONTRACT_NUMBER.LABEL,
    required: isFieldRequired(contractNumberPath),
    extractValue: ({ target: { value } }) => filterContractNumber(value),
    onBlur: () => setPercent(initialValues),
  });

  const currentCompanyPath =
    "project.terminationRequest.brokerageMandate.currentCompany";
  const currentCompanyField = bindField(currentCompanyPath, {
    label: SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL,
    required: isFieldRequired(currentCompanyPath),
    onBlur: () => setPercent(initialValues),
  });

  const addressPath = "project.terminationRequest.address";
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.LABEL,
    required: false,
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "project.terminationRequest.brokerageMandate.lastName";
  const lastNameField = bindField(lastNamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstNamePath = "project.terminationRequest.brokerageMandate.firstName";
  const firstNameField = bindField(firstNamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstNamePath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
    >
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
        }
        subtitle={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.SUBTITLE
        }
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <div className="d-flex justify--between align--center">
              <span>
                {SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
                  .REPRISE_CONTRACT_12_MOIS ||
                  "S'agit-il d'une reprise à la concurrence d'un contrat de plus de 12 mois ?"}
              </span>
              <Switch
                checked={contractMoreThanOneYear}
                onChange={handleContractMoreThan12MonthsChange}
              />
            </div>
          </Col>

          {contractMoreThanOneYear && (
            <Col lg={24} xs={24}>
              <div className="d-flex justify--between align--center">
                <span>
                  {SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
                    .WANTS_MUTUELLE_SOLEIL_RESILIATION ||
                    "Souhaitez-vous confier à la Mutuelle du Soleil la résiliation de votre contrat de complémentaire santé actuel ?"}
                </span>
                <Switch
                  defaultChecked={
                    get(initialValues, "project.fields.selfOccupation") ===
                    false
                  }
                  onChange={(e) => {
                    set(initialValues, "project.fields.selfOccupation", !e);
                  }}
                />
              </div>
            </Col>
          )}

          {contractMoreThanOneYear && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={contractNumberField.path}
                  label={contractNumberField.label}
                  required={contractNumberField.required}
                  disabled={contractNumberField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={SUBSCRIPTION.FORM.CONTRACT_NUMBER.PLACEHOLDER}
                      icon={<MdOutlineFrontHand size={16} />}
                      type="custom"
                      stripSpaces={true}
                      defaultValue={contractNumberField.defaultValue}
                      {...field}
                      onChange={contractNumberField.onChange}
                      onBlur={contractNumberField.onBlur}
                    />
                  )}
                </FormField>
              </Col>

              <Col lg={24} xs={24}>
                <FormField
                  path={currentCompanyField.path}
                  label={currentCompanyField.label}
                  required={currentCompanyField.required}
                  disabled={currentCompanyField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL}
                      icon={<MdOutlineShareLocation size={16} />}
                      type="custom"
                      normalizeText={true}
                      defaultValue={currentCompanyField.defaultValue}
                      {...field}
                      onChange={currentCompanyField.onChange}
                      onBlur={currentCompanyField.onBlur}
                    />
                  )}
                </FormField>
              </Col>

              <Col lg={24} xs={24}>
                <FormField
                  path={addressField.path}
                  label={addressField.label}
                  required={addressField.required}
                  disabled={addressField.disabled}
                >
                  {(field) => (
                    <Input
                      optional={SUBSCRIPTION.FORM.OPTIONAL}
                      placeholder={
                        SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.PLACEHOLDER
                      }
                      icon={<MdOutlinePinDrop size={16} />}
                      type="custom"
                      normalizeText={true}
                      defaultValue={addressField.defaultValue}
                      {...field}
                      onChange={addressField.onChange}
                      onBlur={addressField.onBlur}
                    />
                  )}
                </FormField>
              </Col>

              <Col lg={24} xs={24}>
                <FormField
                  path={lastNameField.path}
                  label={lastNameField.label}
                  required={lastNameField.required}
                  disabled={lastNameField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      type="custom"
                      normalizeText={true}
                      defaultValue={lastNameField.defaultValue}
                      {...field}
                      onChange={lastNameField.onChange}
                      onBlur={lastNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>

              <Col lg={24} xs={24}>
                <FormField
                  path={firstNameField.path}
                  label={firstNameField.label}
                  required={firstNameField.required}
                  disabled={firstNameField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                      icon={<MdOutlineShareLocation size={16} />}
                      type="custom"
                      normalizeText={true}
                      defaultValue={firstNameField.defaultValue}
                      {...field}
                      onChange={firstNameField.onChange}
                      onBlur={firstNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default TerminationRequest;
